export interface MessageBase {
  type: string;
}

export interface MessageRequest extends MessageBase {
  type: 'request';
  id: number | string;
  method: string;
  params?: Record<string, any>;
}

export interface MessageResponse extends MessageBase {
  type: 'response';
  id: number | string;
  code: number; // 0 - success, other - error
  payload?: Record<string, any>;
  error?: any;
}

export interface MessageNotification extends MessageBase {
  type: 'notification';
  method: string;
  payload?: Record<string, any>;
}

type MaybePromise<T> = T | Promise<T>;

export interface BaseBridgeOptions {
  handleRequest?: (
    method: string,
    params?: Record<string, any>
  ) => MaybePromise<Pick<MessageResponse, 'code' | 'payload' | 'error'>>;
}

// Multiple instances of the same bridge will have different ids
let id = 0;

function generateId() {
  return (++id).toString();
}

export abstract class BaseBridge extends EventTarget {
  private handlers: Record<
    string,
    { resolve: (result: any) => void; reject: (error: Error) => void }
  > = {};
  protected options: BaseBridgeOptions;
  constructor(options?: BaseBridgeOptions) {
    super();
    this.options = {
      ...options,
    };
  }
  protected safeJsonParse(data: any) {
    try {
      return typeof data === 'object' || data === undefined ? data : JSON.parse(data);
    } catch (err) {
      console.error('safeJsonParse error:', err);
      return undefined;
    }
  }
  protected safeJsonStringify(data: any) {
    try {
      return typeof data === 'string' ? data : JSON.stringify(data);
    } catch (err) {
      console.error('safeJsonStringify error:', err);
      return undefined;
    }
  }
  protected requestHandler(
    method: string,
    params: Record<string, any> | undefined,
    handler: (data: MessageRequest) => void
  ) {
    return new Promise((resolve, reject) => {
      const id = generateId();

      const requestMsg: MessageRequest = {
        type: 'request',
        id,
        method,
      };

      if (params) {
        requestMsg.params = params;
      }

      try {
        handler(requestMsg);
        this.handlers[id] = { resolve, reject };
      } catch (error) {
        reject(error);
      }
    });
  }
  protected responseHandler(res: MessageResponse) {
    const handler = this.handlers[res.id];
    if (handler) {
      const { resolve, reject } = handler;

      if (res.code > 0) {
        const error =
          res.error instanceof Error
            ? res.error
            : new Error(typeof res.error === 'string' ? res.error : 'Unknown error.', {
                cause: res,
              });
        reject(error);
      } else {
        resolve(res.payload);
      }

      delete this.handlers[res.id];
    }
  }
  protected async handleRequest(requestMsg: MessageRequest): Promise<MessageResponse> {
    const { handleRequest } = this.options;
    if (!handleRequest) {
      throw new Error('handleRequest is not set');
    }
    const { id, method, params } = requestMsg;
    const { code, payload, error } = await handleRequest(method, this.safeJsonParse(params));
    return {
      type: 'response',
      id,
      code,
      payload,
      error,
    };
  }
  protected handleNotification(notificationMsg: MessageNotification) {
    const { method, payload } = notificationMsg;
    return this.dispatchEvent(
      new MessageEvent<Record<string, any>>(method, {
        data: payload,
      })
    );
  }
  abstract request(method: string, params?: Record<string, any> | undefined): Promise<any>;
  abstract notify(method: string, payload?: Record<string, any> | undefined): void;
  abstract [Symbol.dispose](): void;
}
